import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Clock, FileText, Minus, Plus, ArrowRight, Zap, Timer, TrendingUp } from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// 1. Interactive Mini-Room Demo (Wand- & Material-Vorschau)
// ─────────────────────────────────────────────────────────────

export function MiniRoomDemo() {
  const [length, setLength] = useState(8.0);
  const [width, setWidth] = useState(5.0);
  const [height, setHeight] = useState(2.75);
  const [exteriorThickness, setExteriorThickness] = useState(0.365);
  const [interiorThickness, setInteriorThickness] = useState(0.115);
  const [doorWidth, setDoorWidth] = useState(0.985);
  const [windowWidth, setWindowWidth] = useState(1.26);

  const calc = useMemo(() => {
    // Outer wall perimeter minus door & window openings
    const perimeter = 2 * (length + width);
    const openingAreaDoor = doorWidth * 2.135;
    const openingAreaWindow = windowWidth * 1.385 * 2; // 2 windows
    const wallArea = perimeter * height - openingAreaDoor - openingAreaWindow;
    const floorArea = length * width;
    const volume = wallArea * exteriorThickness;
    // Approx material
    const steine = Math.round(wallArea * 16); // ~16 Stk/m² (12DF)
    const paletten = Math.ceil(steine / 60);
    const moertel = Math.ceil(wallArea * 0.2); // ~0.2 Säcke/m²
    return {
      wallArea: wallArea.toFixed(1),
      floorArea: floorArea.toFixed(1),
      volume: volume.toFixed(2),
      steine,
      paletten,
      moertel,
    };
  }, [length, width, height, exteriorThickness, doorWidth, windowWidth]);

  // SVG scale: map meters to 0-360 px
  const maxDim = Math.max(length, width);
  const svgScale = 320 / maxDim;
  const roomWidth = length * svgScale;
  const roomHeight = width * svgScale;
  const offsetX = (380 - roomWidth) / 2;
  const offsetY = (260 - roomHeight) / 2;

  const doorPxWidth = doorWidth * svgScale;
  const windowPxWidth = windowWidth * svgScale;

  return (
    <section className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Spielen Sie mit einem Raum
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto text-sm">
            Eine Mini-Version unserer Engine – ändern Sie Maße und Wandstärken per Slider und sehen Sie sofort,
            wie sich Flächen, Volumen und Materialbedarf anpassen.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
          {/* SVG Room */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 overflow-hidden">
            <svg viewBox="0 0 380 260" className="w-full h-auto">
              <defs>
                <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#27272a" strokeWidth="0.5" />
                </pattern>
                <pattern id="hatch" patternUnits="userSpaceOnUse" width="4" height="4" patternTransform="rotate(45)">
                  <line x1="0" y="0" x2="0" y2="4" stroke="#3b82f640" strokeWidth="1" />
                </pattern>
              </defs>
              <rect width="380" height="260" fill="url(#grid)" opacity="0.4" />

              {/* Exterior walls (hatched) */}
              <rect x={offsetX} y={offsetY} width={roomWidth} height={roomHeight}
                    fill="none" stroke="#3b82f6" strokeWidth="2" />
              <rect x={offsetX - 5} y={offsetY - 5} width={roomWidth + 10} height={roomHeight + 10}
                    fill="url(#hatch)" stroke="#60a5fa" strokeWidth="1.5" />
              <rect x={offsetX} y={offsetY} width={roomWidth} height={roomHeight}
                    fill="#0a0a0a" stroke="#3b82f6" strokeWidth="2" />

              {/* Door (opening + arc) */}
              <rect x={offsetX + roomWidth / 2 - doorPxWidth / 2} y={offsetY + roomHeight - 2}
                    width={doorPxWidth} height={6} fill="#0a0a0a" stroke="none" />
              <path d={`M ${offsetX + roomWidth / 2 - doorPxWidth / 2} ${offsetY + roomHeight}
                        a ${doorPxWidth} ${doorPxWidth} 0 0 1 ${doorPxWidth} 0`}
                    fill="none" stroke="#f59e0b" strokeWidth="1.2" strokeDasharray="2 2" />

              {/* Windows (two on top wall) */}
              {[0.3, 0.7].map((f, i) => (
                <g key={i}>
                  <rect x={offsetX + roomWidth * f - windowPxWidth / 2} y={offsetY - 3}
                        width={windowPxWidth} height={6} fill="#0a0a0a" stroke="#3b82f6" strokeWidth="1" />
                  <line x1={offsetX + roomWidth * f - windowPxWidth / 2} y1={offsetY}
                        x2={offsetX + roomWidth * f + windowPxWidth / 2} y2={offsetY}
                        stroke="#60a5fa" strokeWidth="1.5" strokeDasharray="3 1" />
                </g>
              ))}

              {/* Dimension labels */}
              <rect x={offsetX + roomWidth / 2 - 26} y={offsetY - 28} width={52} height={18} rx="3"
                    fill="#f59e0b" />
              <text x={offsetX + roomWidth / 2} y={offsetY - 15} fill="#fff" fontSize="10"
                    fontWeight="600" textAnchor="middle" fontFamily="'Space Grotesk', monospace">
                {length.toFixed(2)} m
              </text>

              <rect x={offsetX + roomWidth + 6} y={offsetY + roomHeight / 2 - 9} width={52} height={18} rx="3"
                    fill="#f59e0b" />
              <text x={offsetX + roomWidth + 32} y={offsetY + roomHeight / 2 + 4} fill="#fff" fontSize="10"
                    fontWeight="600" textAnchor="middle" fontFamily="'Space Grotesk', monospace">
                {width.toFixed(2)} m
              </text>
            </svg>

            <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-xs">
              <SliderRow label="Länge" unit="m" min={3} max={15} step={0.1} value={length} setter={setLength} />
              <SliderRow label="Breite" unit="m" min={3} max={12} step={0.1} value={width} setter={setWidth} />
              <SliderRow label="Höhe" unit="m" min={2.2} max={3.5} step={0.05} value={height} setter={setHeight} />
              <SliderRow label="AW-Dicke" unit="m" min={0.115} max={0.49} step={0.005}
                         value={exteriorThickness} setter={setExteriorThickness} />
              <SliderRow label="IW-Dicke" unit="m" min={0.1} max={0.24} step={0.005}
                         value={interiorThickness} setter={setInteriorThickness} />
              <SliderRow label="Türbreite" unit="m" min={0.6} max={1.25} step={0.025}
                         value={doorWidth} setter={setDoorWidth} />
              <SliderRow label="Fensterbreite" unit="m" min={0.6} max={2.5} step={0.05}
                         value={windowWidth} setter={setWindowWidth} />
            </div>
          </div>

          {/* Result Panel */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-4">Live-Kalkulation</div>
            <ResultRow label="Wandfläche (netto)" value={calc.wallArea} unit="m²" accent />
            <ResultRow label="Bodenfläche" value={calc.floorArea} unit="m²" />
            <ResultRow label="Wandvolumen (AW)" value={calc.volume} unit="m³" />
            <div className="my-3 border-t border-zinc-800/60" />
            <ResultRow label="Steine (12 DF)" value={calc.steine.toLocaleString('de-DE')} unit="Stk." />
            <ResultRow label="Paletten (60/Pal.)" value={calc.paletten} unit="Stk." />
            <ResultRow label="Mörtel (DBM)" value={calc.moertel} unit="Säcke" />
            <ResultRow label="Verschnitt" value="5" unit="%" />
            <div className="mt-5 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/30 text-[11px] text-blue-200 leading-relaxed">
              Das ist nur ein kleiner Ausschnitt. Auf Grundrissen läuft die Kalkulation
              <strong className="text-blue-100"> vollautomatisch</strong> für alle Wände,
              Räume und Öffnungen im gesamten Gebäude.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SliderRow({ label, unit, min, max, step, value, setter }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-zinc-500 flex items-center justify-between">
        <span>{label}</span>
        <span className="font-mono text-amber-300">{Number(value).toFixed(step < 0.01 ? 3 : 2)} {unit}</span>
      </span>
      <input type="range" min={min} max={max} step={step} value={value}
             onChange={(e) => setter(parseFloat(e.target.value))}
             className="accent-amber-500 w-full h-1" />
    </label>
  );
}

function ResultRow({ label, value, unit, accent }) {
  return (
    <div className="flex items-baseline justify-between py-1.5 border-b border-zinc-800/40 last:border-0">
      <span className="text-zinc-400 text-xs">{label}</span>
      <span className={`font-mono font-semibold ${accent ? 'text-amber-300 text-lg' : 'text-zinc-100 text-sm'}`}>
        {value} <span className="text-zinc-500 text-xs font-normal">{unit}</span>
      </span>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// 2. ROI Calculator
// ─────────────────────────────────────────────────────────────

/* ─── Animated Counter Hook ─── */
function useCounter(target, duration = 1800, active = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) { setVal(0); return; }
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * ease));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, active]);
  return val;
}

/* ─── Process Step Bar ─── */
function StepBar({ label, hours, maxHours, color, delay }) {
  const pct = Math.min((hours / maxHours) * 100, 100);
  return (
    <div className="flex items-center gap-3">
      <span className="font-mono text-[11px] text-zinc-400 w-[110px] text-right shrink-0">{label}</span>
      <div className="flex-1 h-7 bg-zinc-800/40 rounded overflow-hidden relative">
        <div
          className="h-full rounded flex items-center justify-end pr-2 transition-all duration-1000 ease-out"
          style={{
            width: `${pct}%`,
            background: color,
            transitionDelay: `${delay}ms`,
          }}
        >
          <span className="font-mono text-[10px] text-white/90 font-bold whitespace-nowrap">
            {hours < 1 ? `${Math.round(hours * 60)} min` : `${hours}h`}
          </span>
        </div>
      </div>
    </div>
  );
}

export function RoiCalculator({ onCta }) {
  const [perMonth, setPerMonth] = useState(5);
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); io.disconnect(); }
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const manual = { aufmass: 3.0, eintippen: 1.5, pruefen: 0.5, lv: 1.0 };
  const auto = { aufmass: 0.25, eintippen: 0, pruefen: 0.08, lv: 0.17 };
  const manualTotal = manual.aufmass + manual.eintippen + manual.pruefen + manual.lv;
  const autoTotal = auto.aufmass + auto.eintippen + auto.pruefen + auto.lv;
  const savedPerPlan = manualTotal - autoTotal;
  const savedHoursYear = Math.round(savedPerPlan * perMonth * 12);
  const savedDays = Math.round(savedHoursYear / 8);
  const savedMoney = savedHoursYear * 60;

  const cHours = useCounter(savedHoursYear, 2000, visible);
  const cDays = useCounter(savedDays, 2000, visible);
  const cMoney = useCounter(savedMoney, 2200, visible);

  return (
    <section id="roi" className="py-24 px-6" ref={ref}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full mb-4">
            <TrendingUp className="w-3.5 h-3.5 text-amber-400" />
            <span className="font-mono text-[10px] text-amber-300 uppercase tracking-wider">Zeitgewinn-Analyse</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Ihr Team gewinnt Tage zurück
          </h2>
          <p className="text-zinc-400 max-w-lg mx-auto text-sm leading-relaxed">
            Vergleichen Sie den manuellen Aufwand mit der automatisierten Analyse — pro Grundriss.
          </p>
        </div>

        {/* Process Comparison */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Manual */}
          <div className="p-6 bg-zinc-900/60 border border-zinc-800 rounded-2xl">
            <div className="flex items-center gap-2 mb-5">
              <Timer className="w-4 h-4 text-red-400" />
              <span className="font-mono text-sm text-red-300 font-medium">Manueller Prozess</span>
              <span className="ml-auto font-mono text-lg text-red-400 font-bold">{manualTotal}h</span>
            </div>
            <div className="space-y-2.5">
              <StepBar label="Aufmaß vor Ort" hours={manual.aufmass} maxHours={manualTotal} color="linear-gradient(90deg, #ef4444, #dc2626)" delay={0} />
              <StepBar label="Eintippen/Erfassen" hours={manual.eintippen} maxHours={manualTotal} color="linear-gradient(90deg, #f87171, #ef4444)" delay={150} />
              <StepBar label="Prüfung" hours={manual.pruefen} maxHours={manualTotal} color="linear-gradient(90deg, #fca5a5, #f87171)" delay={300} />
              <StepBar label="LV erstellen" hours={manual.lv} maxHours={manualTotal} color="linear-gradient(90deg, #fecaca, #fca5a5)" delay={450} />
            </div>
          </div>

          {/* Automated */}
          <div className="p-6 bg-zinc-900/60 border border-blue-500/20 rounded-2xl">
            <div className="flex items-center gap-2 mb-5">
              <Zap className="w-4 h-4 text-blue-400" />
              <span className="font-mono text-sm text-blue-300 font-medium">Mit LAIzyBlueprint</span>
              <span className="ml-auto font-mono text-lg text-blue-400 font-bold">{Math.round(autoTotal * 60)} min</span>
            </div>
            <div className="space-y-2.5">
              <StepBar label="PDF hochladen" hours={auto.aufmass} maxHours={manualTotal} color="linear-gradient(90deg, #3b82f6, #2563eb)" delay={600} />
              <StepBar label="Automatisch" hours={auto.eintippen || 0.01} maxHours={manualTotal} color="linear-gradient(90deg, #60a5fa, #3b82f6)" delay={750} />
              <StepBar label="KI-Validierung" hours={auto.pruefen} maxHours={manualTotal} color="linear-gradient(90deg, #93c5fd, #60a5fa)" delay={900} />
              <StepBar label="LV-Export" hours={auto.lv} maxHours={manualTotal} color="linear-gradient(90deg, #bfdbfe, #93c5fd)" delay={1050} />
            </div>
          </div>
        </div>

        {/* Animated Counters */}
        <div className="grid grid-cols-3 gap-4 mb-10">
          <div className="text-center p-5 bg-zinc-900/40 border border-zinc-800 rounded-xl">
            <div className="font-mono text-3xl sm:text-4xl font-bold text-amber-400" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {cHours.toLocaleString('de-DE')}
            </div>
            <div className="font-mono text-[10px] text-zinc-500 mt-1 uppercase tracking-wider">Stunden/Jahr gespart</div>
          </div>
          <div className="text-center p-5 bg-zinc-900/40 border border-zinc-800 rounded-xl">
            <div className="font-mono text-3xl sm:text-4xl font-bold text-blue-400" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {cDays}
            </div>
            <div className="font-mono text-[10px] text-zinc-500 mt-1 uppercase tracking-wider">Arbeitstage frei</div>
          </div>
          <div className="text-center p-5 bg-zinc-900/40 border border-zinc-800 rounded-xl">
            <div className="font-mono text-3xl sm:text-4xl font-bold text-emerald-400" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {cMoney.toLocaleString('de-DE')} €
            </div>
            <div className="font-mono text-[10px] text-zinc-500 mt-1 uppercase tracking-wider">Einsparpotenzial</div>
          </div>
        </div>

        {/* Slider + CTA */}
        <div className="max-w-md mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <button onClick={() => setPerMonth(Math.max(1, perMonth - 1))}
              className="w-8 h-8 rounded-lg bg-zinc-800 border border-zinc-700 hover:border-zinc-600 flex items-center justify-center text-zinc-400">
              <Minus className="w-3.5 h-3.5" />
            </button>
            <div className="px-5 py-2 bg-zinc-900 border border-zinc-800 rounded-lg">
              <span className="font-mono text-lg text-white font-bold">{perMonth}</span>
              <span className="font-mono text-xs text-zinc-500 ml-1.5">Aufmaße / Monat</span>
            </div>
            <button onClick={() => setPerMonth(Math.min(50, perMonth + 1))}
              className="w-8 h-8 rounded-lg bg-zinc-800 border border-zinc-700 hover:border-zinc-600 flex items-center justify-center text-zinc-400">
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
          <input type="range" min={1} max={50} step={1} value={perMonth}
            onChange={e => setPerMonth(parseInt(e.target.value))}
            className="w-full h-1 accent-amber-500 cursor-pointer mb-6" />
          <button onClick={onCta} data-testid="roi-cta-btn"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-all hover:shadow-lg hover:shadow-blue-500/20 text-sm">
            Jetzt kostenlos testen <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-[10px] text-zinc-600 mt-4">
            Basierend auf 60 €/h Stundensatz · {manualTotal}h manuell vs. {Math.round(autoTotal * 60)} min automatisiert
          </p>
        </div>
      </div>
    </section>
  );
}
